import type { UploadCpdRow } from '@/features/calendar/api/useUploadCpdPoints'

export type ParsedCpdCsv = {
  headers: string[]
  previewRows: Record<string, string>[]
  uploadRows: UploadCpdRow[]
}

const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, '')

const parseCsvLine = (line: string): string[] => {
  const cells: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]

    if (char === '"') {
      const nextChar = line[i + 1]
      if (inQuotes && nextChar === '"') {
        current += '"'
        i += 1
        continue
      }
      inQuotes = !inQuotes
      continue
    }

    if (char === ',' && !inQuotes) {
      cells.push(current.trim())
      current = ''
      continue
    }

    current += char
  }

  cells.push(current.trim())
  return cells
}

/**
 * Parses a CSV file into upload-ready rows while preserving the raw preview content.
 */
export async function parseCpdCsv(
  file: File,
  selectedEvent?: { id: string | number; title?: string },
): Promise<ParsedCpdCsv> {
  const text = await file.text()
  const sanitized = text.replace(/^\uFEFF/, '')
  const lines = sanitized.split(/\r?\n/).filter(line => line.trim().length > 0)
  if (lines.length <= 1) return { headers: [], previewRows: [], uploadRows: [] }

  const headers = parseCsvLine(lines[0]).map(header => header.trim())
  const headerMap = new Map<string, number>()
  headers.forEach((header, idx) => {
    const normalized = normalizeHeader(header)
    if (!normalized || headerMap.has(normalized)) return
    headerMap.set(normalized, idx)
  })

  const rawRows = lines.slice(1).map(parseCsvLine).filter(row => row.some(cell => cell.trim().length > 0))

  const previewRows = rawRows.map(row => {
    const record: Record<string, string> = {}
    headers.forEach((header, idx) => {
      record[header] = row[idx] ?? ''
    })
    return record
  })

  const getValue = (row: string[], aliases: string[]) => {
    for (const alias of aliases) {
      const normalized = normalizeHeader(alias)
      const index = headerMap.get(normalized)
      if (typeof index === 'number') {
        return (row[index] ?? '').trim()
      }
    }
    return ''
  }

  const uploadRows = rawRows
    .map(row => {
      const memberId = getValue(row, ['memberId', 'member id', 'member', 'member_id'])
      const eventIdRaw = getValue(row, ['eventId', 'event id', 'event', 'event_id'])
      const pointsRaw = getValue(row, ['points', 'point', 'cpd points', 'cpd', 'cpd_points', 'cpdpoints'])
      const awardedOnRaw = getValue(row, ['awardedOn', 'awarded on', 'awarded', 'awarded date', 'date', 'awarded_on'])

      const resolvedMemberId = memberId.trim()
      const resolvedEventId = (eventIdRaw || (selectedEvent ? String(selectedEvent.id) : '')).trim()
      const parsedPoints = Number(pointsRaw)
      const resolvedPoints = Number.isFinite(parsedPoints) ? parsedPoints : 0
      const parsedAwardedOn = awardedOnRaw ? new Date(awardedOnRaw) : null
      const resolvedAwardedOn = parsedAwardedOn && !Number.isNaN(parsedAwardedOn.getTime())
        ? parsedAwardedOn.toISOString()
        : new Date().toISOString()

      return {
        memberId: resolvedMemberId,
        eventId: resolvedEventId,
        points: resolvedPoints,
        email: getValue(row, ['email', 'email address', 'member email']),
        phone: getValue(row, ['phone', 'phone number', 'member phone']),
        eventName: getValue(row, ['eventName', 'event name', 'eventtitle', 'event title']) || (selectedEvent?.title ?? ''),
        awardedOn: resolvedAwardedOn,
      }
    })
    .filter(row => row.memberId.length > 0 && row.eventId.length > 0)

  return { headers, previewRows, uploadRows }
}

export default parseCpdCsv
